import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HistoryIcon from '@mui/icons-material/History';
import apiService from '../services/ApiService';
import authService from '../services/AuthService';
import LoadingSpinner from '../components/LoadingSpinner';
import ResultsTable from '../components/ResultsTable';
import DailyPlayerRanking from '../components/DailyPlayerRanking';
import { debugError } from '../utils/debugUtils';

export default function HistoryPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [topics, setTopics] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [finalResults, setFinalResults] = useState([]);
  const [userWords, setUserWords] = useState([]);
  const [loadingTopics, setLoadingTopics] = useState(true);
  const [loadingResults, setLoadingResults] = useState(false);
  const user = authService.getUser();

  useEffect(() => {
    const load = async () => {
      try {
        const data = await apiService.get('/api/v1/topics/history');
        if (Array.isArray(data) && data.length > 0) {
          setTopics(data);
          const dateParam = searchParams.get('date');
          if (dateParam) {
            const idx = data.findIndex((tp) => tp.date === dateParam);
            setCurrentIndex(idx !== -1 ? idx : 0);
          } else {
            setCurrentIndex(0);
          }
        }
      } catch (err) {
        debugError('[HistoryPage] Error loading topics:', err);
      } finally {
        setLoadingTopics(false);
      }
    };
    load();
  }, []);

  const loadResultsForTopic = useCallback(
    async (topic) => {
      if (!topic) return;
      setLoadingResults(true);
      setFinalResults([]);
      setUserWords([]);
      try {
        const [resultsData, wordsData] = await Promise.all([
          topic.hasResults
            ? apiService.get(`/api/v1/game/final-results/${topic.id}`)
            : Promise.resolve([]),
          user
            ? apiService.get(`/api/v1/game/user-words/${topic.id}`)
            : Promise.resolve([]),
        ]);
        const results = resultsData?.results ?? (Array.isArray(resultsData) ? resultsData : []);
        setFinalResults(Array.isArray(results) ? results : []);
        setUserWords(Array.isArray(wordsData) ? wordsData : []);
      } catch (err) {
        debugError('[HistoryPage] Error loading results:', err);
      } finally {
        setLoadingResults(false);
      }
    },
    [user]
  );

  useEffect(() => {
    if (topics.length > 0) {
      loadResultsForTopic(topics[currentIndex]);
    }
  }, [currentIndex, topics, loadResultsForTopic]);

  const currentTopic = topics[currentIndex];

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (loadingTopics) return <LoadingSpinner />;

  if (topics.length === 0) {
    return (
      <Box className="page-content" sx={{ maxWidth: 700, mx: 'auto', width: '100%' }}>
        <Typography variant="h5" fontWeight={700} textAlign="center" color="white" sx={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
          {t('history.title')}
        </Typography>
        <Card sx={{ width: '100%' }}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <HistoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">{t('history.noHistory')}</Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box className="page-content" sx={{ gap: 2, maxWidth: 900, mx: 'auto', width: '100%' }}>
      <Typography variant="h5" fontWeight={700} textAlign="center" color="white" sx={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
        {t('history.title')}
      </Typography>

      <Card sx={{ width: '100%', flexShrink: 0 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <IconButton
              onClick={() => setCurrentIndex((i) => i + 1)}
              disabled={currentIndex >= topics.length - 1}
              aria-label={t('history.older')}
              sx={{ flexShrink: 0 }}
            >
              <ChevronLeftIcon />
            </IconButton>

            <Box sx={{ textAlign: 'center', flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'capitalize', wordBreak: 'break-word' }}>
                {currentTopic && formatDate(currentTopic.date)}
              </Typography>
              <Typography variant="h6" fontWeight={700} sx={{ wordBreak: 'break-word' }}>
                {currentTopic?.topic}
              </Typography>
            </Box>

            <IconButton
              onClick={() => setCurrentIndex((i) => i - 1)}
              disabled={currentIndex <= 0}
              aria-label={t('history.newer')}
              sx={{ flexShrink: 0 }}
            >
              <ChevronRightIcon />
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
            {(() => {
              let sliceStart = Math.max(0, currentIndex - 2);
              let sliceEnd = Math.min(topics.length, currentIndex + 3);
              if (sliceStart === 0) sliceEnd = Math.min(topics.length, 5);
              else if (sliceEnd === topics.length) sliceStart = Math.max(0, topics.length - 5);
              return topics.slice(sliceStart, sliceEnd).map((tp, i) => {
                const globalIdx = sliceStart + i;
                return { tp, globalIdx };
              })
              .reverse()
              .map(({ tp, globalIdx }) => (
                <Chip
                  key={tp.id}
                  label={new Date(tp.date + 'T00:00:00').toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
                  size="small"
                  variant={globalIdx === currentIndex ? 'filled' : 'outlined'}
                  color={globalIdx === currentIndex ? 'primary' : 'default'}
                  onClick={() => setCurrentIndex(globalIdx)}
                  sx={{ cursor: 'pointer' }}
                />
              ));
            })()}
          </Box>
        </CardContent>
      </Card>

      {loadingResults ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <LoadingSpinner />
        </Box>
      ) : currentTopic?.hasResults ? (
        <Grid container spacing={2} sx={{ width: '100%' }}>
          <Grid item xs={12} md={userWords.length > 0 ? 8 : 12}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  {t('history.top100Title')}
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  {t('history.top100Subtitle')}
                </Typography>
                {finalResults.length > 0 ? (
                  <ResultsTable results={finalResults} yourWords={userWords} showTitle={false} />
                ) : (
                  <Typography color="text.secondary" textAlign="center" py={3}>
                    {t('results.noResults')}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {userWords.length > 0 && (
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={700} gutterBottom>
                    {t('history.yourWordsTitle')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {userWords.map((word, idx) => {
                      const resultEntry = finalResults.find((r) => r.word === word.canonical);
                      const scored = resultEntry && resultEntry.score > 0;
                      return (
                        <Box key={idx}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
                            <Typography sx={{ textTransform: 'capitalize', fontWeight: scored ? 700 : 400 }}>
                              {word.raw}
                            </Typography>
                            {scored ? (
                              <Chip
                                label={`+${resultEntry.score} pts`}
                                size="small"
                                color="success"
                                sx={{ fontWeight: 700 }}
                              />
                            ) : (
                              <Typography variant="caption" color="text.disabled">
                                {t('history.noPoints')}
                              </Typography>
                            )}
                          </Box>
                          {idx < userWords.length - 1 && <Divider />}
                        </Box>
                      );
                    })}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <DailyPlayerRanking date={currentTopic?.date} user={user} />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : (
        <Card sx={{ width: '100%' }}>
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <Typography color="text.secondary">{t('history.resultsNotReady')}</Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
